import React, { useState, useEffect } from 'react';
import { useActiveCareerFocus } from '../../hooks/useCareerFocus';
import { useRubric } from '../../hooks/useRubric';
import { useContentLinks } from '../../hooks/useContentLinks';
import { useToast } from '../../context/ToastContext';
import { CheckIcon } from '@heroicons/react/24/outline';

interface RubricCriteriaSelectorProps {
  cardId?: string;
  onCriteriaUpdated?: (criteriaIds: string[]) => void;
  onChange?: (criteriaIds: string[]) => void;
  initialSelectedCriteria?: string[];
  className?: string;
  sourceType?: 'career_card' | 'note' | 'review';
}

const RubricCriteriaSelector: React.FC<RubricCriteriaSelectorProps> = ({ 
  cardId, 
  onCriteriaUpdated,
  onChange,
  initialSelectedCriteria = [],
  className = '',
  sourceType = 'career_card'
}) => {
  const { showToast } = useToast();
  const { focus } = useActiveCareerFocus();
  const { activeRubric } = useRubric(focus?.id);
  const { 
    createLink, 
    createBulkLinks,
    deleteLinksByCriteria, 
    getLinksForItem,
    isLinking 
  } = useContentLinks();
  
  const [selectedCriteria, setSelectedCriteria] = useState<Set<string>>(new Set(initialSelectedCriteria));
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Load existing rubric criterion links
  useEffect(() => {
    const loadExistingLinks = async () => {
      if (!focus || !activeRubric) {
        setIsLoading(false);
        return;
      }

      // If no cardId, use initial selected criteria
      if (!cardId) {
        setSelectedCriteria(new Set(initialSelectedCriteria));
        setIsLoading(false);
        return;
      }

      try {
        const links = await getLinksForItem(sourceType, cardId, 'source');
        const criterionLinks = links.filter(
          link => link.target_type === 'rubric_criterion'
        );
        
        const linkedCriteriaIds = criterionLinks.map(link => link.target_id);
        setSelectedCriteria(new Set(linkedCriteriaIds));
        setHasChanges(false); // Reset changes flag after loading
      } catch (error) {
        console.error('Failed to load rubric links:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingLinks();
  }, [cardId, focus?.id, activeRubric?.id, sourceType, getLinksForItem]); // Only depend on IDs to prevent unnecessary re-renders
  
  const handleCriterionToggle = (criterionId: string) => {
    const newSelected = new Set(selectedCriteria);
    if (newSelected.has(criterionId)) {
      newSelected.delete(criterionId);
    } else {
      newSelected.add(criterionId);
    }
    setSelectedCriteria(newSelected);
    setHasChanges(true); // Mark that we have changes
    
    // If onChange is provided and no cardId, use onChange instead of saving
    if (onChange && !cardId) {
      onChange(Array.from(newSelected));
    }
  };
  
  const handleSave = async () => {
    if (!activeRubric || isLinking || !cardId) return;

    try {
      // First, remove all existing rubric criterion links for this card
      await deleteLinksByCriteria(sourceType, cardId, 'rubric_criterion');
      
      // Then create new links for each selected criterion
      if (selectedCriteria.size > 0) {
        const links = Array.from(selectedCriteria).map(criterionId => ({
          sourceType: sourceType,
          sourceId: cardId,
          targetType: 'rubric_criterion' as const,
          targetId: criterionId,
          metadata: { 
            focus_id: focus?.id 
          }
        }));
        
        await createBulkLinks(links);
      }
      
      setHasChanges(false); // Reset changes flag after successful save
      if (onCriteriaUpdated) {
        onCriteriaUpdated(Array.from(selectedCriteria));
      }
    } catch (error) {
      console.error('Failed to save rubric links:', error);
      showToast('Failed to update rubric criteria', 'error');
    }
  };
  
  // Auto-save when criteria change (only if we have a cardId)
  useEffect(() => {
    if (!isLoading && activeRubric && cardId && hasChanges) {
      // Debounce the save to prevent rapid fire requests
      const timeoutId = setTimeout(() => {
        handleSave();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedCriteria, hasChanges]);
  
  if (!focus || !activeRubric) {
    return null;
  }
  
  return (
    <div className={`${className}`}>
      <label className={`block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2`}>
        Link to Evaluation Criteria {focus && focus.company && `(${focus.company} - ${focus.role})`}
      </label>
      
      <div className={`bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 border rounded-lg p-3`}>
        {isLoading ? (
          <div className="animate-pulse">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {activeRubric.categories.map((category) => (
              <div key={category.id}>
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
                  {category.name}
                </h4>
                <div className="space-y-2 pl-3">
                  {/* Handle normalized criteria if available */}
                  {category.criteria_items && category.criteria_items.length > 0 ? (
                    category.criteria_items.map((criterion) => (
                      <label
                        key={criterion.id}
                        className={`flex items-start gap-3 p-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          isLinking ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCriteria.has(criterion.id)}
                          onChange={() => handleCriterionToggle(criterion.id)}
                          disabled={isLinking}
                          className="mt-0.5 h-4 w-4 rounded text-blue-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                        <span className="text-sm text-gray-800 dark:text-gray-200">
                          {criterion.text}
                        </span>
                      </label>
                    ))
                  ) : (
                    // Fallback to legacy string array
                    category.criteria?.map((criterionText, index) => {
                      const criterionId = `${category.id}-${index}`;
                      return (
                        <label
                          key={criterionId}
                          className={`flex items-start gap-3 p-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                            isLinking ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCriteria.has(criterionId)}
                            onChange={() => handleCriterionToggle(criterionId)}
                            disabled={isLinking}
                            className="mt-0.5 h-4 w-4 rounded text-blue-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                          />
                          <span className="text-sm text-gray-800 dark:text-gray-200">
                            {criterionText}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {selectedCriteria.size > 0 && (
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-3">
            {selectedCriteria.size} criteri{selectedCriteria.size !== 1 ? 'a' : 'on'} selected
          </p>
        )}
      </div>
    </div>
  );
};

export default RubricCriteriaSelector;